import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import Seo from "../components/Seo";
import Navbar from "../components/Navbar";
import { toast } from "sonner";
import { MapPin, ChevronLeft } from "lucide-react";
import { getErrorMessage } from "../lib/errors";
import GoogleLoginButton from "../components/GoogleLoginButton";
import { useTranslation } from "react-i18next";

export default function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const next = new URLSearchParams(loc.search).get("next");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      const dest = next || (u.role === "admin" ? "/admin" : u.role === "worker" ? "/worker" : "/dashboard");
      nav(dest);
    } catch (err) {
      toast.error(getErrorMessage(err, "Login failed"));
    } finally { setLoading(false); }
  };

  return (
    <><Seo title="Log In" description="Log in to your Universal Tree account to book services or manage your professional profile." noindex />
    <div className="min-h-screen flex flex-col bg-[#FAF9F6]">
      <div className="hidden md:block"><Navbar /></div>
      {/* Mobile app bar */}
      <div className="mobile-top-bar flex items-center gap-3 px-4 py-3 md:hidden">
        <button onClick={() => nav(-1)} className="grid h-9 w-9 shrink-0 place-items-center rounded-full hover:bg-[#F3F1ED]">
          <ChevronLeft size={22} />
        </button>
        <span className="grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-full bg-[#050706] shadow-[0_4px_12px_rgba(28,69,50,0.18)]">
           <img src="/theuniversaltree-logo.jpeg" alt="" className="h-full w-full object-cover rounded-full" />
        </span>
        <h1 className="font-display text-base font-semibold text-[#1A1D1A]">Universal Tree</h1>
      </div>
      <main className="flex-1 grid lg:grid-cols-2">
        <div className="flex items-center justify-center px-6 py-16">
          <form onSubmit={submit} className="w-full max-w-md">
            <p className="text-xs uppercase tracking-[0.25em] text-[#D9734E] font-bold">{t("login.subtitle")}</p>
            <h1 className="font-display text-4xl lg:text-5xl font-light tracking-tight mt-3">{t("login.title")}</h1>
            <p className="mt-3 text-sm text-[#646964]">{t("login.description")}</p>

            <div className="mt-10 space-y-5">
              <div>
                <label className="text-xs uppercase tracking-wider text-[#646964] mb-2 block">{t("login.form.email")}</label>
                <Input type="email" required value={email} onChange={e => setEmail(e.target.value)} data-testid="login-email" className="rounded-sm"/>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#646964] mb-2 block">{t("login.form.password")}</label>
                <Input type="password" required value={password} onChange={e => setPassword(e.target.value)} data-testid="login-password" className="rounded-sm"/>
              </div>
              <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full rounded-sm bg-[#1C4532] hover:bg-[#143324] text-white py-6">
                {loading ? t("login.loggingIn") : t("login.continue")}
              </Button>

              <div className="flex items-center gap-3 my-1">
                <div className="flex-1 h-px bg-[#E2E0D9]"/>
                <span className="text-xs uppercase tracking-wider text-[#646964]">{t("common.or")}</span>
                <div className="flex-1 h-px bg-[#E2E0D9]"/>
              </div>
              <GoogleLoginButton/>

              <p className="text-sm text-center text-[#646964]">
                {t("login.newHere")} <Link to="/signup" className="text-[#1C4532] font-medium hover:underline">{t("login.createAccount")}</Link>
              </p>
            </div>
          </form>
        </div>
        <div className="hidden lg:block bg-[#1C4532] relative overflow-hidden">
          <img src="https://images.pexels.com/photos/7045322/pexels-photo-7045322.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-50"/>
          <div className="absolute inset-0 bg-gradient-to-tr from-[#1C4532] to-transparent"/>
          <div className="relative h-full flex flex-col justify-end p-16 text-white">
            <MapPin size={32} className="text-[#D9734E] mb-6"/>
            <p className="font-display text-3xl leading-snug">{t("login.hero.title")}</p>
            <p className="text-sm text-[#FAF9F6]/70 mt-3 max-w-sm">{t("login.hero.description")}</p>
          </div>
        </div>
      </main>
    </div>
    </>
  );
}
